import { SaleStatus } from 'models/SaleStatus';
import { imagesUrlsAsArray } from 'utils/cdn';
import { NO_ELEMENT, rm, withCurrentPage } from 'utils';
import { KeyValueData, StructuredData } from 'components/StructuredData';
import { DetailedProduct } from 'models/DetailedProduct';
import { ProductPageModel } from 'models/ProductPageModel';
import { AppShellManifest } from 'models/AppShellManifest';
import { ProductDetailedMeta } from 'models/ProductMeta';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import { configuratorModifier, getConfigurator } from './configurators/selector';
import { MediaWidget, WidgetType } from 'models/ListViewProduct';
import { BhImageType } from 'models/BhImageType';
import { BhVideo } from 'models/BhVideo';
import { useSelector } from 'react-redux';
import { useMemoCompare } from 'hooks/useMemoCompare';
import {
  getDetailedMeta,
  getProduct,
  getBreadCrumbsStructured,
  getTotalNumberOfReviews,
  getAverageReviewScore,
  getRedReviews
} from './selector';
import { getAppShell } from 'store/appShell/selector';
import { formatISO } from 'date-fns';
import { RedReviewSummary } from 'models/RedReviewSummary';
import { RedReview } from 'models/RedReview';

const IMAGE_DIMENSION = {
  width: 414,
  height: 414
};

const DESKTOP_IMAGE_DIMENSION = {
  width: 673,
  height: 673
};

const HUGE_IMAGE_DIMENSION = {
  width: 1080,
  height: 1080
};

const PRICE_PRECISION = 2;

const Types = {
  Product: 'Product',
  Offer: 'Offer',
  Organization: 'Organization',
  AggregateRating: 'AggregateRating',
  Review: 'Review',
  Rating: 'Rating',
  Thing: 'Thing',
  Video: 'VideoObject',
  Person: 'Person'
};

const SchemaTypes = {
  NewCondition: 'http://schema.org/NewCondition',
  InStock: 'http://schema.org/InStock',
  OutOfStock: 'http://schema.org/OutOfStock',
  BackOrder: 'http://schema.org/BackOrder'
};

const getOffer = ({
  product,
  meta,
  currencyCode,
  config
}: {
  product: DetailedProduct;
  meta: ProductDetailedMeta;
  currencyCode: string;
  config?: ConfiguratorBase;
}) => {
  const MAX_YEAR = 9999;

  const price = (
    (product.price.lowestPossiblePrice ?? product.price.listPrice) * configuratorModifier(config)
  ).toFixed(PRICE_PRECISION);

  const offers: KeyValueData = {
    '@type': Types.Offer,
    price,
    priceCurrency: currencyCode,
    itemCondition: SchemaTypes.NewCondition,
    seller: {
      '@type': Types.Organization,
      name: rm('site_master_name')
    }
  };

  if (meta.isBackOrder) {
    offers.availability = SchemaTypes.BackOrder;
    offers.availabilityDate = formatISO(new Date(meta.publishedSaleDate));
  } else {
    offers.availability =
      meta.saleStatus === SaleStatus.Active ? SchemaTypes.InStock : SchemaTypes.OutOfStock;
  }
  const campaignEnd = new Date(meta.campaignEnd);
  if (!isNaN(campaignEnd.getFullYear()) && campaignEnd.getFullYear() < MAX_YEAR) {
    offers.priceValidUntil = campaignEnd.toISOString();
  }

  return offers;
};

const RatingMinMax = {
  Min: 1,
  Max: 5
};

const getReviewData = (
  reviewAvgScore: number,
  reviews: RedReview[],
  totalNumberOfReviews?: number
) => {
  const aggregateRating = {
    '@type': Types.AggregateRating,
    ratingValue: reviewAvgScore,
    reviewCount: totalNumberOfReviews,
    bestRating: RatingMinMax.Max,
    worstRating: RatingMinMax.Min
  };

  const review = reviews.map(review => ({
    '@type': Types.Review,
    author: { '@type': Types.Person, name: review.author },
    datePublished: new Date(review.date).toString(),
    description: review.comment,
    reviewRating: {
      '@type': Types.Rating,
      bestRating: RatingMinMax.Max,
      worstRating: RatingMinMax.Min,
      ratingValue: review.score
    }
  }));

  return { aggregateRating, review };
};

const getVideoData = (product: DetailedProduct) => {
  if (!Array.isArray(product.widgets) || product.widgets.length <= 0) {
    return [];
  }
  const mediaWidgets = product.widgets.filter(x => x.type === WidgetType.Media) as MediaWidget[];
  const videoItems = mediaWidgets.map(x => x.media.filter(y => y.type === BhImageType.Video));

  // eslint-disable-next-line prefer-spread
  const flatVideoItems = [].concat.apply([], videoItems) as BhVideo[];

  const videos = flatVideoItems.map(widget => ({
    '@type': Types.Video,
    name: htmlEncode(product.title),
    description: htmlEncode(product.shortDescription.content || product.title),
    uploadDate: new Date().toString(),
    thumbnailUrl: widget.thumbnailUrl,
    contentUrl: widget.videoUrl
  }));

  return { videos };
};

interface ProductStructuredData {
  product?: ProductPageModel['product'];
  meta?: ProductDetailedMeta;
  reviews: ProductPageModel['redReviews'];
  totalNumberOfReviews: RedReviewSummary['numberOfReviews'];
  reviewAvgScore: RedReviewSummary['averageScore'];
  config: ProductPageModel['configurator'];
  appShell: AppShellManifest;
  breadCrumbsStructured?: ProductPageModel['breadCrumbsStructured'];
}

const getProductPageStructuredData = (
  args: ProductStructuredData | undefined
): KeyValueData | undefined => {
  if (!args || !args.product || !args.meta) {
    return undefined;
  }
  const {
    appShell,
    config,
    meta,
    product,
    reviews,
    totalNumberOfReviews,
    breadCrumbsStructured,
    reviewAvgScore
  } = args;
  const { cdnUrl, site } = appShell.links;

  const data: KeyValueData = {
    '@type': Types.Product,
    name: htmlEncode(product.title),
    image: imagesUrlsAsArray({
      product,
      dimensions: IMAGE_DIMENSION,
      desktopDimensions: DESKTOP_IMAGE_DIMENSION,
      hugeDimensions: HUGE_IMAGE_DIMENSION,
      cdnUrl
    }),
    description: htmlEncode(product.shortDescription.content),
    url: `${site}${product.url}`,
    mpn: meta.supplierProductNumber,
    brand: {
      '@type': Types.Thing,
      name: product.brandName
    },
    offers: getOffer({ product, meta, currencyCode: appShell.currencyCode, config }),
    breadCrumbsStructured: breadCrumbsStructured
  };

  if (Array.isArray(reviews?.reviews) && reviews.reviews.length > 0) {
    const { aggregateRating, review } = getReviewData(
      reviewAvgScore,
      reviews.reviews,
      totalNumberOfReviews
    );
    data.aggregateRating = aggregateRating;
    data.review = review;
  }

  if (Array.isArray(product.widgets) && product.widgets.length > 0) {
    data.video = getVideoData(product);
  }
  return data;
};

const htmlEncode = (s: string | undefined) => {
  if (s === undefined) return s;

  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/'/g, '&#39;')
    .replace(/"/g, '&#34;');
};

const useProductStructuredData = () => {
  const product = useSelector(withCurrentPage(getProduct));
  const meta = useSelector(getDetailedMeta);
  const reviews = useSelector(withCurrentPage(getRedReviews));
  const totalNumberOfReviews = useSelector(getTotalNumberOfReviews);
  const config = useSelector(withCurrentPage(getConfigurator));
  const appShell = useSelector(getAppShell);
  const breadCrumbsStructured = useSelector(getBreadCrumbsStructured);
  const reviewAvgScore = useSelector(getAverageReviewScore);

  const [data] = useMemoCompare(
    [
      getProductPageStructuredData({
        product,
        meta,
        reviews,
        totalNumberOfReviews,
        reviewAvgScore,
        config,
        appShell,
        breadCrumbsStructured
      }),
      product?.productId,
      product?.variantId,
      meta,
      reviews.reviews?.length,
      totalNumberOfReviews
    ],
    (
      [, prevProductId, prevVariantId, prevReviews, prevTotalNumberOfReviews],
      [, nextPoductId, nextVariantId, nextReviews, nextTotalNumberOfReviews]
    ) =>
      nextPoductId === prevProductId &&
      nextVariantId === prevVariantId &&
      nextReviews === prevReviews &&
      nextTotalNumberOfReviews === prevTotalNumberOfReviews
  );
  return data;
};

export const ProductPageStructuredData = () => {
  const data = useProductStructuredData();

  return data ? <StructuredData data={data} /> : NO_ELEMENT;
};
